import React, { useState } from "react"

import { TooltipPortal } from "@radix-ui/react-tooltip"
import {
  Briefcase,
  Calendar,
  Check,
  CircleCheck,
  CircleDashed,
  CircleSlash,
  Clock,
  Database,
  FileCheck,
  IdCard,
  Mail,
  MessageSquare,
  MoreVertical,
  PauseCircle,
  Puzzle,
  TestTube2,
  Upload,
  UserCheck,
  UserX,
  Workflow,
  X,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { StageDialogType } from "@/config/stageDialogConfig"
import { allReasons } from "@/config/stageDialogConfig"
import { useAuth } from "@/contexts/AuthContext"
import { type ActiveEngagements, useFetchActiveEngagements } from "@/hooks/useActiveEngagements"
import { useEmailIndividualApplication } from "@/hooks/useEmailIndividualApplication"

import { useUpdateApplicationStage } from "../hooks/useUpdateApplicationStage"
import { Application } from "../types/application"
import ApplicationStageDialog from "./applications/ApplicationStageDialog"
import RejectWarningDialog from "./applications/RejectWarningDialog"
import { PermissionGuard } from "./common/PermissionGuard"
import { stagesConfig } from "./job-postings/KanbanStageConfig"
import { Badge } from "./ui/badge"
import { ClampedText } from "./ui/clampedText"
import { formatDate } from "./ui/formatDate"
import { Score } from "./ui/score"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"

interface ApplicationCardProps {
  application: Application
  onDragStart: () => void
  onDragEnd: () => void
  onStatusChange?: (id: string, status: string) => void
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  onDragStart,
  onDragEnd,
  onStatusChange,
}) => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { mutate: updateStage, isPending: isUpdating } = useUpdateApplicationStage()
  const { mutate: sendEmail, isPending: isSendingEmail } = useEmailIndividualApplication()

  // State for confirmation dialogs
  const [showHoldConfirmation, setShowHoldConfirmation] = useState(false)
  const [showStageDialog, setShowStageDialog] = useState(false)
  const [stageDialogType, setStageDialogType] = useState<StageDialogType>("rejection")
  const [pendingReject, setPendingReject] = useState<{
    data: { stage: string; reason: string; note: string }
    engagements: ActiveEngagements
  }>(undefined)
  const [isCheckingEngagements, setIsCheckingEngagements] = useState(false)

  const fetchActiveEngagements = useFetchActiveEngagements()

  const renderStageReason = (stageReason: string) => {
    const reason = allReasons.find((reason) => reason.value === stageReason)
    return reason?.label || stageReason
  }

  const handleClick = () => {
    navigate(`/applications/${application.id}`)
  }

  const handleSendReminderEmail = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (application.application_id) {
      const redirectUrl = import.meta.env.VITE_FRONTEND_URL
        ? `${import.meta.env.VITE_FRONTEND_URL}/auth-callback`
        : "https://deliver.hived.space/auth-callback"

      sendEmail({
        applicationId: application.application_id,
        redirectUrl,
      })
    }
  }

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation()
    setStageDialogType("rejection")
    setShowStageDialog(true)
  }

  const handleHold = (e: React.MouseEvent) => {
    e.stopPropagation()
    setStageDialogType("hold")
    setShowStageDialog(true)
  }

  const handleConfirmHold = () => {
    updateStage(
      {
        id: application.id,
        stage: "hold",
      },
      {
        onSuccess: () => {
          toast.success("Application On Hold", {
            description: `${application.name} has been placed on hold`,
          })
          if (onStatusChange) {
            onStatusChange(application.id, "hold")
          }
        },
        onError: () => {
          toast.error("Update Failed", {
            description: "Failed to update application status. Please try again.",
          })
        },
      },
    )
    setShowHoldConfirmation(false)
  }

  // Format the time since stage change as a relative duration
  const formatTimeSinceChange = (timestamp: string | null | undefined): string => {
    if (!timestamp) return ""

    const now = new Date()
    const changedDate = new Date(timestamp)
    const diffMs = now.getTime() - changedDate.getTime()

    // Convert to seconds, minutes, hours, days
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffDays > 0) {
      return `${diffDays}d`
    } else if (diffHours > 0) {
      return `${diffHours}h`
    } else if (diffMins > 0) {
      return `${diffMins}m`
    } else {
      return `${diffSecs}s`
    }
  }

  const submitStageChange = (data: { stage: string; reason: string; note: string }) => {
    const isRejection = data.stage === "rejected"

    updateStage(
      {
        id: application.id,
        stage: data.stage,
        stage_reason: data.reason,
        stage_note: data.note,
      },
      {
        onSuccess: () => {
          toast.success(isRejection ? "Application Rejected" : "Application On Hold", {
            description: isRejection
              ? `${application.name} was rejected`
              : `${application.name} was put on hold`,
          })
          if (onStatusChange) {
            onStatusChange(application.id, data.stage)
          }
        },
        onError: () => {
          toast.error("Update Failed", {
            description: "Failed to update application status. Please try again.",
          })
        },
      },
    )
  }

  const handleStageDialogConfirm = async (data: {
    stage: string
    reason: string
    note: string
  }) => {
    // Close the stage dialog first
    setShowStageDialog(false)

    // For rejections, warn if the person still has open timesheets / claimed shifts.
    if (data.stage === "rejected") {
      setIsCheckingEngagements(true)
      try {
        const engagements = await fetchActiveEngagements(application.personId)
        if (engagements.hasActiveEngagements) {
          setPendingReject({ data, engagements })
          return
        }
      } catch (_error) {
        toast.warning("Could not check active engagements", {
          description: "Proceeding with the rejection without the active work check.",
        })
      } finally {
        setIsCheckingEngagements(false)
      }
    }

    submitStageChange(data)
  }

  const handleConfirmRejectAnyway = () => {
    if (pendingReject) {
      submitStageChange(pendingReject.data)
      setPendingReject(undefined)
    }
  }

  const getSegmentLabel = (application: Application) => {
    const stageConfig = stagesConfig.find((stage) => stage.id === application.stage)
    const segments = stageConfig?.segments || []
    const segmentConfig = segments.find((segment) => segment.id === application.segment)
    return segmentConfig?.label || application.segment
  }

  // Format a human-friendly date/time string
  const formatDateTime = (value?: string | null) => {
    if (!value) return ""
    const d = new Date(value)
    if (isNaN(d.getTime())) return value
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Determine if a date is in the past (strictly before now)
  const isPastDate = (value?: string | null) => {
    if (!value) return false
    const d = new Date(value)
    if (isNaN(d.getTime())) return false
    return d.getTime() < Date.now()
  }

  // Simple wrapper to show children only when application's stage matches the provided stage
  const StageData: React.FC<{
    stage: string | string[]
    children: React.ReactNode
  }> = ({ stage, children }) => {
    const stages = Array.isArray(stage) ? stage : [stage]
    return stages.includes(application.stage) ? <>{children}</> : null
  }

  return (
    <>
      <div
        className="application-card"
        draggable={user?.role !== "DSP"}
        onDragStart={() => onDragStart()}
        onDragEnd={onDragEnd}
      >
        <div className="flex items-center justify-between">
          <div onClick={handleClick} className="cursor-pointer flex-grow">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                {/* Person Attached Indicator - Small icon before name */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-help">
                        {application.personId ? (
                          <UserCheck className="w-3.5 h-3.5 text-green-600" />
                        ) : (
                          <UserX className="w-3.5 h-3.5 text-amber-600" />
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm font-semibold mb-1">
                          {application.personId ? "Person Attached" : "No Person"}
                        </p>
                        <p className="text-xs text-palette-jungleGreen600">
                          {application.personId
                            ? "This application has a person record linked"
                            : "No person record linked to this application yet"}
                        </p>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>

                <h3 className="font-medium text-secondary">
                  {application.dataCollectionOnly
                    ? application.airtablePersonId || "No Airtable ID"
                    : application.name}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {application.dataCollectionOnly ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1 px-2 py-1 bg-palette-jungleGreen100 rounded-md border border-palette-jungleGreen200 cursor-help">
                          <Database className="w-3.5 h-3.5 text-palette-jungleGreen600" />
                        </div>
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent className="max-w-xs">
                          <p className="text-sm">
                            Data Collection Job - Used to collect information from existing drivers
                          </p>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <>
                    {application.changedStageAt && (
                      <div className="flex items-center gap-1 text-xs text-palette-jungleGreen700 bg-palette-jungleGreen100 px-2 py-1 rounded-md">
                        <Clock className="w-3 h-3" />
                        <span>{formatTimeSinceChange(application.changedStageAt)}</span>
                      </div>
                    )}
                    <PermissionGuard
                      action="update"
                      resource="applications"
                      scopes={["any", "own"]}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onClick={handleSendReminderEmail}
                            disabled={isSendingEmail || !application.application_id}
                          >
                            <Mail className="mr-2 h-4 w-4" />
                            <span>{isSendingEmail ? "Sending..." : "Send reminder email"}</span>
                          </DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>Move to</DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              <DropdownMenuItem onClick={handleHold} disabled={isUpdating}>
                                <PauseCircle className="mr-2 h-4 w-4 text-amber-600" />
                                <span>Hold</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={handleReject} disabled={isUpdating}>
                                <X className="mr-2 h-4 w-4 text-red-600" />
                                <span>Reject</span>
                              </DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </PermissionGuard>
                  </>
                )}
              </div>
            </div>
            {!application.dataCollectionOnly && (
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500 mt-1">
                <IdCard className="h-4 w-4 flex-shrink-0" />
                {application.application_id}
              </div>
            )}
            {!application.dataCollectionOnly && (
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500 mt-1">
                <Briefcase className="h-4 w-4 flex-shrink-0" />
                <span>{application.jobPosting}</span>
              </div>
            )}

            {application.segment && (
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500 mt-1">
                <Workflow className="h-4 w-4 flex-shrink-0" />
                {getSegmentLabel(application)}
              </div>
            )}

            <StageData stage="orientation">
              <div className="flex items-center gap-2 text-sm mt-1">
                <Calendar className="h-4 w-4 flex-shrink-0 text-palette-jungleGreen500" />
                <div className="flex items-center gap-2">
                  {application.orientationBooked ? (
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isPastDate(application?.orientationBookingDate)
                            ? "text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none"
                            : "text-green-600 bg-green-50 pointer-events-none"
                        }
                        title={
                          isPastDate(application?.orientationBookingDate)
                            ? "Date has passed"
                            : "Upcoming date"
                        }
                      >
                        {formatDateTime(application?.orientationBookingDate)}
                      </Badge>
                      {application.orientationAttended === true && (
                        <Badge
                          className="text-green-600 bg-green-50 pointer-events-none"
                          title="Attended"
                        >
                          <Check className="h-3 w-3" />
                        </Badge>
                      )}
                      {application.orientationAttended === false && (
                        <Badge
                          className="text-red-600 bg-red-50 pointer-events-none"
                          title="Did not attend"
                        >
                          <X className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge className="text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none">
                      Not booked
                    </Badge>
                  )}
                </div>
              </div>

              <TooltipProvider delayDuration={200}>
                <div className="flex items-center gap-2 mt-1">
                  <FileCheck className="h-4 w-4 text-palette-jungleGreen500" />

                  {/* Upload badge */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Badge
                          className={
                            application.allUploaded
                              ? "text-green-500 bg-green-50 pointer-events-none"
                              : "text-amber-600 bg-amber-50 pointer-events-none"
                          }
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          {application.uploadedCount}/{application.totalRequiredDocs}
                        </Badge>
                      </div>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent
                        side="top"
                        align="start"
                        className="max-w-sm z-[9999] shadow-lg p-3 rounded-md"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h4 className="font-semibold mb-2">Upload Status</h4>
                        <ul className="text-xs space-y-1">
                          {application.requiredDocsDetailed.map((doc) => (
                            <li key={doc.id} className="flex items-center gap-1">
                              {doc.uploaded ? (
                                <CircleCheck className="text-green-500 h-4 w-4 flex-shrink-0" />
                              ) : (
                                <CircleDashed className="text-palette-jungleGreen500 h-4 w-4 flex-shrink-0" />
                              )}
                              {doc.title}
                            </li>
                          ))}
                        </ul>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>

                  {/* Verification badge */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Badge
                          className={
                            application.uploadedDocsDetailed.some(
                              (doc) => doc.status === "rejected",
                            )
                              ? "text-red-500 bg-red-50 pointer-events-none"
                              : application.verifiedCount === application.uploadedCount &&
                                  application.uploadedCount > 0
                                ? "text-green-500 bg-green-50 pointer-events-none"
                                : "text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none"
                          }
                        >
                          <Check className="h-3 w-3 mr-1" />
                          {application.verifiedCount}/{application.uploadedCount}
                        </Badge>
                      </div>
                    </TooltipTrigger>
                    {application.uploadedDocsDetailed.length > 0 && (
                      <TooltipPortal>
                        <TooltipContent
                          side="top"
                          align="start"
                          className="max-w-sm z-[9999] shadow-lg p-3 rounded-md"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h4 className="font-semibold mb-2">Verification Status</h4>
                          <ul className="text-xs space-y-1">
                            {application.uploadedDocsDetailed.map((doc) => (
                              <li key={doc.id} className="flex items-center gap-1">
                                {doc.status === "verified" ? (
                                  <CircleCheck className="text-green-500 h-4 w-4 flex-shrink-0" />
                                ) : doc.status === "rejected" ? (
                                  <CircleSlash className="text-red-500 h-4 w-4 flex-shrink-0" />
                                ) : (
                                  <CircleDashed className="h-4 w-4 flex-shrink-0" />
                                )}{" "}
                                {doc.title}
                              </li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </TooltipPortal>
                    )}
                  </Tooltip>
                </div>
              </TooltipProvider>
            </StageData>

            <StageData stage="vehicle-collection">
              <div className="flex items-center gap-2 text-sm mt-1">
                <Calendar className="h-4 w-4 text-palette-jungleGreen500" />
                <div className="flex items-center gap-2">
                  {application.vehicleCollectionBooked ? (
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isPastDate(application?.vehicleCollectionBookingDate)
                            ? "text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none"
                            : "text-green-600 bg-green-50 pointer-events-none"
                        }
                        title={
                          isPastDate(application?.vehicleCollectionBookingDate)
                            ? "Date has passed"
                            : "Upcoming date"
                        }
                      >
                        {formatDateTime(application?.vehicleCollectionBookingDate)}
                      </Badge>
                      {application.vehicleCollectionAttended === true && (
                        <Badge
                          className="text-green-600 bg-green-50 pointer-events-none"
                          title="Attended"
                        >
                          <Check className="h-3 w-3" />
                        </Badge>
                      )}
                      {application.vehicleCollectionAttended === false && (
                        <Badge
                          className="text-red-600 bg-red-50 pointer-events-none"
                          title="Did not attend"
                        >
                          <X className="h-3 w-3" />
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge className="text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none">
                      Not booked
                    </Badge>
                  )}
                </div>
              </div>
            </StageData>

            <StageData stage="trial">
              <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500 mt-1">
                <Puzzle className="h-4 w-4 text-palette-jungleGreen500" />
                Trial blocks: {application.trialBlockCount}
              </div>
            </StageData>

            <StageData stage="driver-onroad-test">
              <div className="flex items-center gap-2 text-sm mt-1">
                <TestTube2 className="h-4 w-4 text-palette-jungleGreen500" />

                {application.driverOnroadTestStatus === "completed" ? (
                  application.driverOnroadTestResult === "pass" ? (
                    <Badge className="bg-green-50 pointer-events-none">
                      <div className="flex items-center gap-2 text-sm text-green-500">
                        Score: <Score value={application.driverOnroadTestScore} />
                      </div>
                    </Badge>
                  ) : (
                    <Badge className="bg-red-50 pointer-events-none">
                      <div className="flex items-center gap-2 text-sm text-red-500">
                        Score: <Score value={application.driverOnroadTestScore} />
                      </div>
                    </Badge>
                  )
                ) : application.driverOnroadTestStatus === "in_progress" ? (
                  <Badge className="bg-palette-jungleGreen000 pointer-events-none">
                    <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500">
                      In Progress
                    </div>
                  </Badge>
                ) : (
                  <Badge className="bg-palette-jungleGreen100 pointer-events-none">
                    <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500">
                      Not Started
                    </div>
                  </Badge>
                )}
              </div>
            </StageData>

            <StageData stage="orientation-quiz">
              <div className="flex items-center gap-2 text-sm mt-1">
                <TestTube2 className="h-4 w-4 text-palette-jungleGreen500" />
                {application.orientationQuizStatus === "completed" ? (
                  application.orientationQuizResult === "pass" ? (
                    <Badge className="bg-green-50 pointer-events-none">
                      <div className="flex items-center gap-2 text-sm text-green-500">
                        Score: <Score value={application.orientationQuizScore} />
                      </div>
                    </Badge>
                  ) : (
                    <Badge className="bg-red-50 pointer-events-none">
                      <div className="flex items-center gap-2 text-sm text-red-500">
                        Score: <Score value={application.orientationQuizScore} />
                      </div>
                    </Badge>
                  )
                ) : application.orientationQuizStatus === "in_progress" ? (
                  <Badge className="bg-amber-50 pointer-events-none">
                    <div className="flex items-center gap-2 text-sm text-amber-500">
                      In Progress
                    </div>
                  </Badge>
                ) : (
                  <Badge className="bg-palette-jungleGreen100 pointer-events-none">
                    <div className="flex items-center gap-2 text-sm text-palette-jungleGreen500 ">
                      Not Started
                    </div>
                  </Badge>
                )}
              </div>
            </StageData>

            <StageData stage="complete">
              <div className="flex items-center gap-2 text-sm mt-1">
                <Calendar className="h-4 w-4 text-palette-jungleGreen500" />
                {application.onboardedAt ? (
                  <Badge className="text-green-600 bg-green-50 pointer-events-none">
                    {formatDate(application.onboardedAt)}
                  </Badge>
                ) : (
                  <Badge className="text-palette-jungleGreen700 bg-palette-jungleGreen100 pointer-events-none">
                    Not Onboarded
                  </Badge>
                )}
              </div>
            </StageData>

            <StageData stage="hold">
              <div className="flex items-center gap-2 text-sm mt-1">
                <PauseCircle className="shrink-0 h-4 w-4 text-palette-jungleGreen500" />
                <ClampedText
                  className="text-amber-500 font-bold max-w-xs"
                  text={renderStageReason(application.stageReason || "") || "No reason provided"}
                />
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <MessageSquare className="shrink-0 h-4 w-4 text-palette-jungleGreen500" />
                <ClampedText
                  className="text-palette-jungleGreen500 max-w-xs"
                  text={application.stageNote || "No note provided"}
                />
              </div>
            </StageData>

            <StageData stage="rejected">
              <div className="flex items-center gap-2 text-sm mt-1">
                <X className="shrink-0 h-4 w-4 text-palette-jungleGreen500" />
                <ClampedText
                  className="text-red-500 font-bold max-w-xs"
                  text={renderStageReason(application.stageReason || "") || "No reason provided"}
                />
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <MessageSquare className="shrink-0 h-4 w-4 text-palette-jungleGreen500" />
                <ClampedText
                  className="text-palette-jungleGreen500 max-w-xs"
                  text={application.stageNote || "No note provided"}
                />
              </div>
            </StageData>
          </div>
        </div>
      </div>

      {/* Hold confirmation dialog */}
      <AlertDialog open={showHoldConfirmation} onOpenChange={setShowHoldConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to place {application.name} on hold?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowHoldConfirmation(false)} disabled={isUpdating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmHold} disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unified stage dialog (rejection or hold) */}
      <ApplicationStageDialog
        open={showStageDialog}
        onOpenChange={setShowStageDialog}
        type={stageDialogType}
        applicantName={application.name}
        onConfirm={handleStageDialogConfirm}
        isUpdating={isUpdating || isCheckingEngagements}
      />

      {pendingReject && (
        <RejectWarningDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingReject(undefined)
          }}
          applicantName={application.name}
          engagements={pendingReject.engagements}
          onConfirm={handleConfirmRejectAnyway}
          isConfirming={isUpdating}
        />
      )}
    </>
  )
}

export default ApplicationCard
